import { stripHtml } from "../components/molecules/Composer/sanitize";

const defined = (value) => value !== undefined && value !== null;

const coerceToString = (value) => (defined(value) ? String(value) : value);

const normalizeReactionEntry = (reaction) => {
    if (!reaction || typeof reaction !== "object") {
        return null;
    }
    const emoji = typeof reaction.emoji === "string" ? reaction.emoji.trim() : "";
    if (!emoji) {
        return null;
    }
    const count = Number.isFinite(Number(reaction.count)) ? Number(reaction.count) : 0;
    const reacted = Boolean(reaction.reacted);
    return { emoji, count, reacted };
};

export const normalizeMessageReactions = (reactions) => {
    if (!Array.isArray(reactions)) {
        return [];
    }
    const normalized = reactions
        .map(normalizeReactionEntry)
        .filter((reaction) => reaction && reaction.emoji);

    const unique = [];
    const seen = new Set();
    normalized.forEach((reaction) => {
        if (!seen.has(reaction.emoji)) {
            seen.add(reaction.emoji);
            unique.push(reaction);
        }
    });
    return unique;
};

const getFirstAvailable = (source, keys) => {
    if (!source || typeof source !== "object") {
        return undefined;
    }
    for (const key of keys) {
        if (Object.prototype.hasOwnProperty.call(source, key) && defined(source[key])) {
            return source[key];
        }
    }
    return undefined;
};

export const normalizeTimestamp = (raw) => {
    if (!defined(raw)) {
        return null;
    }

    const unwrap = (value) => {
        if (!defined(value)) {
            return null;
        }
        if (value && typeof value === "object") {
            if (defined(value.$date)) {
                return unwrap(value.$date);
            }
            if (defined(value.epochSecond)) {
                const millis = Number(value.epochSecond) * 1000 + Math.floor((value.nano || 0) / 1e6);
                return new Date(millis).toISOString();
            }
        }
        if (value instanceof Date) {
            return new Date(value.getTime()).toISOString();
        }
        if (typeof value === "number") {
            return new Date(value).toISOString();
        }
        if (typeof value === "string") {
            const parsed = Date.parse(value);
            if (!Number.isNaN(parsed)) {
                return new Date(parsed).toISOString();
            }
        }
        return null;
    };

    return unwrap(raw);
};

const SAFE_IDENTIFIER_KEYS = ["chatId", "channelId", "groupId", "senderId"];
const DEFAULT_ID_KEYS = ["id", "messageId", "uuid", "_id"];
const DEFAULT_TIMESTAMP_KEYS = ["timestamp", "createdAt", "sentAt"];
const REPLY_PREVIEW_ID_KEYS = ["messageId", "id", "_id"];

const normalizeReplyPreview = (replyTo) => {
    if (!replyTo || typeof replyTo !== "object") {
        return null;
    }

    const messageId = getFirstAvailable(replyTo, REPLY_PREVIEW_ID_KEYS);
    const timestamp = normalizeTimestamp(
        getFirstAvailable(replyTo, DEFAULT_TIMESTAMP_KEYS)
    );
    const normalized = {
        ...replyTo,
        messageId: defined(messageId) ? coerceToString(messageId) : messageId,
    };

    if (Object.prototype.hasOwnProperty.call(replyTo, "senderId")) {
        normalized.senderId = coerceToString(replyTo.senderId);
    }
    if (defined(timestamp)) {
        normalized.timestamp = timestamp;
    }

    return normalized;
};

export const normalizeChatMessage = (message, options = {}) => {
    if (!message || typeof message !== "object") {
        return message;
    }

    const {
        idKeys = DEFAULT_ID_KEYS,
        timestampKeys = DEFAULT_TIMESTAMP_KEYS,
        defaultDeliveryStatus = "DELIVERED",
        ensureSeenByArray = false,
        identifierKeys = SAFE_IDENTIFIER_KEYS,
    } = options;

    const idValue = getFirstAvailable(message, idKeys);
    const timestampValue = getFirstAvailable(message, timestampKeys);

    const normalized = {
        ...message,
        id: defined(idValue) ? coerceToString(idValue) : idValue,
    };

    // Only set timestamp if a valid value is present. Do not clobber with null/undefined.
    const normalizedTs = normalizeTimestamp(timestampValue);
    if (defined(normalizedTs)) {
        normalized.timestamp = normalizedTs;
    }

    identifierKeys.forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(message, key)) {
            normalized[key] = coerceToString(message[key]);
        }
    });

    if (Object.prototype.hasOwnProperty.call(message, "replyToMessageId")) {
        normalized.replyToMessageId = coerceToString(message.replyToMessageId);
    }

    if (Object.prototype.hasOwnProperty.call(message, "replyTo")) {
        normalized.replyTo = normalizeReplyPreview(message.replyTo);
    }

    if (Object.prototype.hasOwnProperty.call(message, "seenBy") || ensureSeenByArray) {
        const rawSeenBy = Array.isArray(message.seenBy) ? message.seenBy : [];
        normalized.seenBy = rawSeenBy
            .filter((entry) => entry !== undefined && entry !== null)
            .map((entry) => String(entry));
    }

    if (
        Object.prototype.hasOwnProperty.call(message, "deliveryStatus") ||
        defaultDeliveryStatus !== undefined
    ) {
        const rawStatus = defined(message.deliveryStatus)
            ? message.deliveryStatus
            : defaultDeliveryStatus;
        normalized.deliveryStatus = defined(rawStatus)
            ? String(rawStatus).toUpperCase()
            : rawStatus;
    }

    normalized.reactions = normalizeMessageReactions(message.reactions);

    // Ensure commentCount is present on initial load. Prefer server-provided value;
    // otherwise derive from embedded comments array if available.
    if (Object.prototype.hasOwnProperty.call(message, "commentCount") && Number.isFinite(Number(message.commentCount))) {
        normalized.commentCount = Number(message.commentCount);
    } else if (Array.isArray(message.comments)) {
        normalized.commentCount = message.comments.length;
    }

    return normalized;
};

const toEpoch = (value) => {
    if (!defined(value)) {
        return Number.NaN;
    }
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? Number.NaN : parsed;
};

const compareMessagesChronologically = (a, b) => {
    const timeA = toEpoch(a?.timestamp);
    const timeB = toEpoch(b?.timestamp);

    if (Number.isNaN(timeA) && Number.isNaN(timeB)) {
        const idA = coerceToString(a?.id ?? "");
        const idB = coerceToString(b?.id ?? "");
        return idA.localeCompare(idB);
    }
    if (Number.isNaN(timeA)) {
        return -1;
    }
    if (Number.isNaN(timeB)) {
        return 1;
    }
    if (timeA === timeB) {
        const idA = coerceToString(a?.id ?? "");
        const idB = coerceToString(b?.id ?? "");
        return idA.localeCompare(idB);
    }
    return timeA - timeB;
};

export const sortMessagesChronologically = (messages) => {
    if (!Array.isArray(messages)) {
        return [];
    }
    return [...messages].sort(compareMessagesChronologically);
};

export const upsertMessage = (messages, incoming) => {
    if (!incoming || !defined(incoming.id)) {
        return messages;
    }

    const normalizedId = coerceToString(incoming.id);
    const index = messages.findIndex((message) => coerceToString(message.id) === normalizedId);
    if (index >= 0) {
        const next = messages.slice();
        const prev = messages[index];
        const merged = { ...prev, ...incoming };
        // Preserve original timestamp when incoming lacks a valid one
        if (!defined(incoming.timestamp)) {
            merged.timestamp = prev.timestamp;
        }
        // Never mutate identity fields during upsert
        merged.id = prev.id;
        if (defined(prev.chatId)) merged.chatId = prev.chatId;
        if (defined(prev.senderId)) merged.senderId = prev.senderId;
        next[index] = merged;
        return next;
    }

    return sortMessagesChronologically([...messages, incoming]);
};

export const mergeMessageReactions = (existing, incoming, options = {}) => {
    const baseExisting = Array.isArray(existing) ? existing : [];
    const normalizedIncoming = normalizeMessageReactions(incoming);
    const existingMap = new Map(baseExisting.map((reaction) => [reaction.emoji, reaction]));
    const {
        currentUserId = null,
        actorId = null,
        emoji: targetEmoji = null,
        action = null,
    } = options;

    return normalizedIncoming.map((reaction) => {
        const previous = existingMap.get(reaction.emoji);
        let reacted = previous?.reacted ?? false;
        if (
            currentUserId != null &&
            actorId != null &&
            String(actorId) === String(currentUserId) &&
            (targetEmoji == null || reaction.emoji === targetEmoji)
        ) {
            reacted = action === "ADD";
        }
        return { ...reaction, reacted };
    });
};

const DEFAULT_MESSAGE_PREVIEW = "No messages yet";
const PREVIEW_ELLIPSIS_RESERVE = 3;

const ATTACHMENT_DEFINITIONS = [
    { label: "image", selectors: ["img"] },
    { label: "video", selectors: ["video"] },
    { label: "audio", selectors: ["audio"] },
    { label: "media", selectors: ["canvas"] },
    { label: "file", selectors: ["object", "embed", "iframe"] },
];

const ATTACHMENT_FALLBACK_PATTERNS = [
    { label: "image", regex: /<\s*img\b[^>]*>/gi },
    { label: "video", regex: /<\s*video\b[^>]*>/gi },
    { label: "audio", regex: /<\s*audio\b[^>]*>/gi },
    { label: "media", regex: /<\s*canvas\b[^>]*>/gi },
    { label: "file", regex: /<\s*(?:object|embed|iframe)\b[^>]*>/gi },
];

const countAttachmentElements = (html) => {
    const counts = {};
    if (!html || typeof html !== "string") {
        return counts;
    }

    const doc = (typeof window !== "undefined" && window.document)
        ? window.document
        : (typeof document !== "undefined" ? document : null);

    if (doc && doc.createElement) {
        const container = doc.createElement("template");
        container.innerHTML = html;
        const root = container.content || container;
        ATTACHMENT_DEFINITIONS.forEach(({ label, selectors }) => {
            let labelCount = counts[label] || 0;
            selectors.forEach((selector) => {
                const nodes = root.querySelectorAll
                    ? root.querySelectorAll(selector)
                    : [];
                labelCount += nodes.length;
            });
            if (labelCount > 0) {
                counts[label] = labelCount;
            }
        });
        return counts;
    }

    ATTACHMENT_FALLBACK_PATTERNS.forEach(({ label, regex }) => {
        const matches = html.match(regex);
        if (matches && matches.length > 0) {
            counts[label] = (counts[label] || 0) + matches.length;
        }
    });

    return counts;
};

const formatAttachmentLabel = (type, count) => {
    const plural = count > 1;
    switch (type) {
        case "image":
            return plural ? `${count} image attachments` : "Image attachment";
        case "video":
            return plural ? `${count} video attachments` : "Video attachment";
        case "audio":
            return plural ? `${count} audio attachments` : "Audio attachment";
        case "file":
            return plural ? `${count} file attachments` : "File attachment";
        case "media":
            return plural ? `${count} media attachments` : "Media attachment";
        default:
            return plural ? `${count} attachments` : "Attachment";
    }
};

const getAttachmentPreviewPlaceholder = (html) => {
    const counts = countAttachmentElements(html);
    const entries = Object.entries(counts).filter(([, count]) => count > 0);
    if (!entries.length) {
        return null;
    }

    const total = entries.reduce((sum, [, count]) => sum + count, 0);

    if (entries.length === 1) {
        const [type, count] = entries[0];
        return formatAttachmentLabel(type, count);
    }

    return formatAttachmentLabel("media", total);
};

export const formatMessagePreview = (input, options = {}) => {
    const { fallback = DEFAULT_MESSAGE_PREVIEW, maxLength = 64 } = options;
    let raw = "";
    if (typeof input === "string") {
        raw = input;
    } else if (input !== undefined && input !== null) {
        raw = String(input);
    }

    let working = raw;
    if (working) {
        working = stripHtml(working).replace(/\s+/g, " ").trim();
    }

    const attachmentPlaceholder = working ? null : getAttachmentPreviewPlaceholder(raw);
    const base = working || attachmentPlaceholder || fallback;
    if (base.length <= maxLength) {
        return base;
    }
    const sliceLength = Math.max(0, maxLength - PREVIEW_ELLIPSIS_RESERVE);
    if (sliceLength <= 0) {
        return "…";
    }
    return `${base.slice(0, sliceLength)}…`;
};

export default normalizeChatMessage;
